<script lang="ts" setup>
import type { THEME } from '@pkg/config/misc/theme';
import FormsBlock from '@pkg/modular-blocks/blocks/inner/Forms/FormsBlock.vue';
import FaqBlock from '@pkg/modular-blocks/blocks/inner/FAQ/FaqBlock.vue';
import ModularBlocksRenderer from '@pkg/modular-blocks/frontend/ModularBlocksRenderer.vue';
import type { SupportPath } from '@pkg/sanity-utils/types/support';
import { alternateTheme } from '@pkg/utilities/themes';
import type { Ref } from 'vue';
import { FormFieldSelect } from './form-fields';
import LayoutSection from '~/app/components/blocks/LayoutSection.vue';
import { useSupportPaths } from '~/app/features/supportPaths/useSupportPaths';

const props = defineProps<{
  theme: THEME;
  supportPath: SupportPath;
}>();

const activeFaq: Ref<string> = useState<string>('activeFaq');

const {
  choice1,
  choice2,
  pathData,
  selection1,
  selection2,
  selection3,
  choicesToSelectOptions,
} = useSupportPaths(props.supportPath);

const extraFields = computed(() => ({
  item1Choice: selection1.value,
  item2Choice: selection2.value,
  item3Choice: selection3.value,
}));
</script>

<template>
  <div>
    <PartialSection :theme="props.theme">
      <div class="wrapper max-w-[384px] box-content">
        <div class="space-y-[1.5rem]">
          <FormFieldSelect
            v-if="props.supportPath?.itemChoices"
            name="item1Choice"
            :options="choicesToSelectOptions(props.supportPath.itemChoices)"
            :label="props.supportPath?.itemTitle || undefined"
            :placeholder="props.supportPath?.itemPlaceholder || undefined"
          />
          <FormFieldSelect
            v-if="choice1?.itemChoices"
            name="item2Choice"
            :options="choicesToSelectOptions(choice1.itemChoices)"
            :label="choice1.itemTitle || undefined"
            :placeholder="choice1?.itemPlaceholder || undefined"
          />
          <FormFieldSelect
            v-if="choice2?.itemChoices"
            name="item3Choice"
            :options="choicesToSelectOptions(choice2.itemChoices)"
            :label="choice2.itemTitle || undefined"
            :placeholder="choice2?.itemPlaceholder || undefined"
          />
        </div>
      </div>
    </PartialSection>
    <LayoutSection :theme="props.theme" indent="both">
      <template #left>
        <BaseTitle :pre-title="pathData.faqPreTitle" :title="pathData.faqTitle" />
      </template>
      <template #right>
        <FaqBlock v-if="pathData.faqItems" :items="pathData.faqItems" />
      </template>
    </LayoutSection>

    <LayoutSection
      v-if="pathData?.formToDisplay && activeFaq && pathData?.formTriggers?.includes(activeFaq)"
      id="form-section"
      :theme="alternateTheme(props.theme)"
      indent="left"
      has-image="right"
    >
      <template #left>
        <BaseTitle tag="h2" :pre-title="pathData.formPreTitle" :title="pathData.formTitle" class="mb-[24px]" />
        <FormsBlock :form="pathData.formToDisplay" :modular-renderer="ModularBlocksRenderer" :extra-fields="extraFields" />
      </template>
      <template #right>
        <div class="hidden tablet-wide:block">
          <SectionImages v-if="pathData.formImage" :image="pathData.formImage" alignment="right" loading="lazy" />
        </div>
      </template>
    </LayoutSection>
  </div>
</template>
